import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Save, RotateCcw } from 'lucide-react';

type RationaleDefaultsPayload = {
  defaultText: string;
  activityMap: Record<string, string>;
};

export default function AdminRationaleDefaultsScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [defaultText, setDefaultText] = useState('');
  const [draftMap, setDraftMap] = useState<Record<string, string>>({});

  const { data, isLoading, error, refetch } = useQuery<RationaleDefaultsPayload>({
    queryKey: ['/api/admin/value-assessment/rationale-defaults'],
    queryFn: async () => {
      try {
        return await apiRequest('/api/admin/value-assessment/rationale-defaults');
      } catch {
        // Fallback to public read endpoint so the page can still render defaults.
        return await apiRequest('/api/value-assessment/rationale-defaults');
      }
    },
  });

  React.useEffect(() => {
    if (!data) return;
    setDefaultText(data.defaultText || '');
    setDraftMap(data.activityMap || {});
  }, [data]);

  const activityEntries = useMemo(() => {
    const entries = Object.entries(draftMap || {}).sort(([a], [b]) => a.localeCompare(b));
    if (!search.trim()) return entries;
    const term = search.toLowerCase();
    return entries.filter(([activity]) => activity.toLowerCase().includes(term));
  }, [draftMap, search]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: RationaleDefaultsPayload = {
        defaultText,
        activityMap: draftMap,
      };
      return apiRequest('/api/admin/value-assessment/rationale-defaults', {
        method: 'PUT',
        body: payload,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/value-assessment/rationale-defaults'] });
      toast({ title: 'Saved', description: 'Rationale defaults have been updated.' });
    },
    onError: () => {
      toast({ title: 'Save failed', description: 'Could not update rationale defaults.', variant: 'destructive' });
    },
  });

  if (isLoading) {
    return <div className="text-gray-300">Loading rationale defaults...</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">Rationale Defaults</h1>
        <p className="text-gray-400 mt-1">Manage default rationale text used by Value Assessment activity and pillar rationale modals.</p>
      </div>

      {error && (
        <Card className="bg-[#2a1c1c] border-[#7f1d1d]">
          <CardContent className="py-4 flex items-center justify-between">
            <div className="text-sm text-red-200">
              Failed to load defaults from admin endpoint. You may be seeing fallback data.
            </div>
            <Button type="button" variant="outline" className="border-red-400 text-red-100" onClick={() => refetch()}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="text-white">Global Fallback Default</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Label className="text-gray-300">Used when an activity key is missing</Label>
          <Textarea
            value={defaultText}
            onChange={(e) => setDefaultText(e.target.value)}
            className="min-h-[90px] bg-redwoodInputFieldBg border-[#334155] text-white"
          />
        </CardContent>
      </Card>

      <Card className="bg-[#0F172A] border-[#334155]">
        <CardHeader>
          <CardTitle className="text-white">Per-Activity Defaults</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search activity"
              className="max-w-md bg-redwoodInputFieldBg border-[#334155] text-white"
            />
            <span className="text-sm text-gray-400">{activityEntries.length} activities</span>
          </div>

          <div className="max-h-[520px] overflow-auto rounded border border-[#334155]">
            <Table>
              <TableHeader>
                <TableRow className="border-[#334155]">
                  <TableHead className="text-gray-300">Activity</TableHead>
                  <TableHead className="text-gray-300">Default Rationale</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activityEntries.map(([activity, rationale]) => (
                  <TableRow key={activity} className="border-[#334155] align-top">
                    <TableCell className="text-sm text-gray-200 w-[38%]">{activity}</TableCell>
                    <TableCell>
                      <Textarea
                        value={rationale || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setDraftMap((prev) => ({ ...prev, [activity]: value }));
                        }}
                        className="min-h-[96px] bg-redwoodInputFieldBg border-[#334155] text-white"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          className="border-[#334155] text-gray-300"
          onClick={() => {
            setDefaultText(data?.defaultText || '');
            setDraftMap(data?.activityMap || {});
          }}
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          Reset Changes
        </Button>
        <Button
          type="button"
          className="bg-primary hover:bg-primaryHover text-forestBlack"
          onClick={() => saveMutation.mutate()}
          disabled={saveMutation.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          {saveMutation.isPending ? 'Saving...' : 'Save Defaults'}
        </Button>
      </div>
    </div>
  );
}
